/**
 * PersonPickerModal — the one layout of the JD's people pickers: Delegate this JD, Share drafts and Send for Approval
 * (wireframe jobs-library-ownership-v3.html; Ownership design, parts 2, 3 and 8). SbModal with the description, a
 * labelled SbSelect (searchable by name or role), the message under it (nobody available, the list failed) and Cancel /
 * the action at the bottom; the action stays disabled until someone is picked. Each picker loads its own list and
 * passes its wording; this only lays it out, so the three look and read the same.
 *   open, title, description (node), label, placeholder, options [{ value, label }], selected, onSelect(value | null)
 *   loading — the list is loading (a spinner in place of the select)
 *   message, messageTestId — the line under the select (none: null)
 *   busy — the action is in flight; locked — the pick is fixed (no clear, no change)
 *   cancelLabel, confirmLabel, canConfirm, onCancel(), onConfirm()
 */

import React from 'react';
import PropTypes from 'prop-types';
import SbModal from 'uiCore/SbModal/SbModal';
import SbButton from 'uiCore/SbButton/SbButton';
import SbSelect from 'uiCore/SbSelect/SbSelect';
import { SbSpin } from 'uiCore';
import { PickerDescriptionStyled, PickerLabelStyled, PickerMessageStyled } from './PersonPickerModal.styles';

const PersonPickerModal = ({
  open, title, description = null, label, placeholder, options, selected = null, onSelect, loading = false,
  message = null, messageTestId = undefined, busy = false, locked = false, cancelLabel = 'Cancel', confirmLabel,
  canConfirm = true, onCancel, onConfirm,
}) => (
  <SbModal
    open={open}
    title={title}
    onCancel={busy ? undefined : onCancel}
    maskClosable={false}
    wrapClassName="jd-confirm-modal"
    destroyOnClose
    footer={[
      <SbButton key="cancel" buttonType="secondary" disabled={busy} onClick={onCancel}>{cancelLabel}</SbButton>,
      <SbButton
        key="confirm"
        buttonType="primary"
        loading={busy}
        disabled={!selected || !canConfirm || busy}
        onClick={onConfirm}
      >
        {confirmLabel}
      </SbButton>,
    ]}
  >
    {description && <PickerDescriptionStyled>{description}</PickerDescriptionStyled>}
    <PickerLabelStyled>{label}</PickerLabelStyled>
    {loading ? (
      <SbSpin size="small" />
    ) : (
      <SbSelect
        options={options}
        value={selected || undefined}
        onChange={(value) => onSelect(value || null)}
        placeholder={placeholder}
        showSearch
        optionFilterProp="label"
        disabled={!options.length || busy || locked}
        allowClear={!locked}
        style={{ width: '100%' }}
        getPopupContainer={(trigger) => trigger.parentNode}
      />
    )}
    {message && <PickerMessageStyled data-testid={messageTestId}>{message}</PickerMessageStyled>}
  </SbModal>
);

PersonPickerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.node,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })).isRequired,
  selected: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  message: PropTypes.string,
  messageTestId: PropTypes.string,
  busy: PropTypes.bool,
  locked: PropTypes.bool,
  cancelLabel: PropTypes.string,
  confirmLabel: PropTypes.string.isRequired,
  canConfirm: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default PersonPickerModal;
